/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Grid, Paper, Typography, Chip, Switch, FormControlLabel, Alert, Dialog, DialogTitle, DialogContent, IconButton, List, ListItem, ListItemText, Divider } from "@mui/material";
import { Info as InfoIcon, Close as CloseIcon } from "@mui/icons-material";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { useMapDisplay } from "./hooks";
import { getMapLegendItems, filterOutlierMarkers, calculateMapBounds, getMarkerTooltipData, IDeviceMarker } from "./helpers";
import DeviceDrawer from "../../../components/Drawers/DeviceDrawer";
import Connected from "../../../components/Shared/Icons/Connected";
import Disconnect from "../../../components/Shared/Icons/Disconnected";

const GOOGLE_MAPS_API_KEY = (window as any).GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyAOxaq2JZHNi0euMmO2a2zk_-uHdKFu9HE";

const containerStyle = {
  width: "100%",
  height: "calc((100vh - 300px) * 0.85)",
  minHeight: "425px",
};

// Use default Google Maps style for full color map
const fullColorStyle = undefined;

// Dark mode map style: reduced saturation/contrast for a calmer monochrome look
const darkMonochromeMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#F2EFE9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#F2EFE9" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7B868C" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#D8D2C8" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#E9E4DB" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DCE7D8" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#DDD7CE" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#E6E1D8" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#E4DFD6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#AAD3DF" }] },
] as any[];

const MapDisplay: React.FC = () => {
  const [showAllDevices, setShowAllDevices] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(2);
  const [showDevicesDialog, setShowDevicesDialog] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<IDeviceMarker | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const previousMarkersRef = useRef<string>("");
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  const mapStyles = useMemo(() => (isDarkMode ? darkMonochromeMapStyle : fullColorStyle), [isDarkMode]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDarkMode(root.classList.contains("dark"));
    });

    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  
  const {
    markers,
    isLoading,
    selectedRowData,
    rowClicked,
    handleMarkerClick,
    handleCloseButtonClicked,
    fetchSerialNumberForMarker,
    devicesCount,
    totalDevices,
    devicesWithoutLocation,
  } = useMapDisplay();

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const legendItems = useMemo(() => getMapLegendItems(), []);

  // Always calculate filtered markers to know if there are outliers
  const filteredResult = useMemo(() => {
    if (markers.length === 0) {
      return {
        visibleMarkers: [],
        outlierMarkers: [],
        filteredCount: 0
      };
    }
    return filterOutlierMarkers(markers, 500, 2); // 500km max distance, min 2 devices for cluster
  }, [markers]);

  // Determine which markers to display based on toggle
  const displayMarkers = showAllDevices ? markers : filteredResult.visibleMarkers;
  const hasOutliers = filteredResult.filteredCount > 0;

  // Recalculate map bounds whenever display markers change
  useEffect(() => {
    if (displayMarkers.length > 0 && isInitialLoad) {
      const bounds = calculateMapBounds(displayMarkers);
      setMapCenter(bounds.center);
      setMapZoom(bounds.zoom);
      setIsInitialLoad(false);
    }
  }, [displayMarkers, isInitialLoad]);

  // Auto-fit map bounds when markers change (after initial load)
  useEffect(() => {
    if (!map || displayMarkers.length === 0 || isInitialLoad) {
      return;
    }

    // Create a unique key from marker positions to detect actual changes
    const currentMarkersKey = displayMarkers
      .map(m => `${m.objectKey}-${m.position.lat}-${m.position.lng}`)
      .sort()
      .join('|');
    
    // Only auto-zoom if markers actually changed (not just re-rendered)
    if (previousMarkersRef.current === currentMarkersKey) {
      return;
    }
    
    console.log('[MapDisplay] Markers changed, auto-fitting bounds');
    previousMarkersRef.current = currentMarkersKey;

    // Small delay to ensure map is ready
    const timeoutId = setTimeout(() => {
      if (map && displayMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        displayMarkers.forEach((marker) => {
          bounds.extend(marker.position);
        });
        
        // Add padding for better visibility
        const padding = { top: 100, right: 100, bottom: 100, left: 100 };
        map.fitBounds(bounds, padding);
        
        console.log(`[MapDisplay] Fitted bounds for ${displayMarkers.length} markers`);
      }
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [displayMarkers, map, isInitialLoad]);

  if (loadError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography color="error">Error loading Google Maps</Typography>
      </Box>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (devicesCount === 0) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
        <Typography variant="h6" color="textSecondary">
          No devices with location data found
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {totalDevices > 0
            ? `${totalDevices} device(s) found, but none have valid latitude/longitude coordinates`
            : "No devices match the current filters"}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {rowClicked && selectedRowData && <DeviceDrawer data={selectedRowData} closedButtonClicked={handleCloseButtonClicked} />}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">
                Showing {displayMarkers.length} device{displayMarkers.length !== 1 ? "s" : ""} on map
                {totalDevices > devicesCount && (
                  <>
                    {" ("}  
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      {totalDevices - devicesCount} without location data
                      <IconButton
                        size="small"
                        onClick={() => setShowDevicesDialog(true)}
                        sx={{ p: 0, ml: 0.5 }}
                      >
                        <InfoIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Box>
                    {")"}
                  </>
                )}
              </Typography>
              {hasOutliers && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showAllDevices}
                      onChange={(e) => setShowAllDevices(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {showAllDevices ? "Show US devices only" : "Show all devices (incl. outside US)"}
                    </Typography>
                  }
                />
              )}
            </Box>
            <Box display="flex" gap={1}>
              {legendItems.map((item) => (
                <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      border: "1px solid #333",
                    }}
                  />
                  <Typography variant="caption">{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {hasOutliers && !showAllDevices && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {filteredResult.filteredCount} device{filteredResult.filteredCount !== 1 ? "s" : ""}{" "}
              {filteredResult.filteredCount !== 1 ? "are" : "is"} located outside US coordinates and{" "}
              {filteredResult.filteredCount !== 1 ? "were" : "was"} filtered out. 
              Toggle the switch above to display all devices globally.
            </Alert>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              position: 'relative',
              '& .gm-style .gm-ui-hover-effect': {
                display: 'none !important',
              },
              '& .gm-style .gm-style-iw-c': {
                padding: '0 !important',
                backgroundColor: 'var(--popover) !important',
                color: 'var(--popover-foreground) !important',
                border: '1px solid var(--border) !important',
                borderRadius: '8px !important',
              },
              '& .gm-style .gm-style-iw-d': {
                overflow: 'hidden !important',
                backgroundColor: 'transparent !important',
                padding: '0 !important',
                margin: '0 !important',
              },
              '& .gm-style .gm-style-iw-d > div': {
                padding: '0 !important',
                margin: '0 !important',
              },
              '& .gm-style .gm-style-iw-tc::after': {
                background: 'var(--popover) !important',
              },
            }}
          >
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onLoad={(mapInstance) => {
                console.log('[MapDisplay] Map instance loaded');
                mapInstance.setOptions({
                  styles: mapStyles,
                  mapTypeControl: false,
                  zoomControl: false,
                  rotateControl: false,
                  fullscreenControl: true,
                  cameraControl: false,
                });
                mapRef.current = mapInstance;
                setMap(mapInstance);
              }}
              onUnmount={() => {
                console.log('[MapDisplay] Cleaning up map instance');
                mapRef.current = null;
                setMap(null);
              }}
              options={{
                mapTypeId: 'roadmap',
                streetViewControl: false,
                mapTypeControl: false,
                zoomControl: false,
                rotateControl: false,
                fullscreenControl: true,
                cameraControl: false,
                styles: mapStyles,
              }}
            >
              {displayMarkers.map((marker) => {
                return (
                  <Marker
                    key={marker.objectKey}
                    position={marker.position}
                    onClick={() => handleMarkerClick(marker)}
                    onMouseOver={() => {
                      setHoveredMarker(marker);
                      fetchSerialNumberForMarker(marker);
                    }}
                    onMouseOut={() => setHoveredMarker(null)}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                        <svg width="31" height="31" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="15.5" cy="15.5" r="10" fill="${marker.color}" stroke="white" stroke-width="2"/>
                        </svg>
                      `)}`,
                      scaledSize: new window.google.maps.Size(31, 31),
                      anchor: new window.google.maps.Point(15.5, 15.5),
                    }}
                  />
                );
              })}
              
              {/* InfoWindow for hover tooltip */}
              {hoveredMarker && (
                <InfoWindow
                  position={hoveredMarker.position}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -15.5),
                    disableAutoPan: true,
                  }}
                >
                  <Box className="min-w-[220px] px-2 pb-2 pt-0 text-popover-foreground">
                    <Box className="m-0 p-0 text-sm font-bold leading-tight text-popover-foreground">
                      {getMarkerTooltipData(hoveredMarker).name}
                    </Box>
                    <Divider sx={{ mt: 0, mb: 0.5, borderColor: 'var(--border)' }} />
                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" className="text-muted-foreground">
                            Serial Number:
                          </Typography>
                          <Typography variant="body2" className="text-popover-foreground">
                            {getMarkerTooltipData(hoveredMarker).serialNumber === "N/A" 
                              ? hoveredMarker.objectKey 
                              : getMarkerTooltipData(hoveredMarker).serialNumber}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ minHeight: 24 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              lineHeight: 0,
                            }}
                          >
                            {getMarkerTooltipData(hoveredMarker).connectionStatus === "Connected" ? (
                              <Connected className="text-green-500" />
                            ) : (
                              <Disconnect
                                color="#C60C30"
                                style={{ transform: "scale(0.7)", transformOrigin: "center", display: "block" }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" className="text-popover-foreground" sx={{ lineHeight: 1.25 }}>
                            {getMarkerTooltipData(hoveredMarker).connectionStatus}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {getMarkerTooltipData(hoveredMarker).criticalAlarmCount > 0 && (
                            <Box display="flex" alignItems="center" sx={{ minHeight: 24 }}>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  lineHeight: 0,
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: 18, color: '#D13C59' }} />
                              </Box>
                              <Typography
                                variant="body2"
                                className="text-popover-foreground"
                                sx={{ lineHeight: 1.25, ml: 1, width: 64 }}
                              >
                                Critical:
                              </Typography>
                              <Chip
                                label={getMarkerTooltipData(hoveredMarker).criticalAlarmCount}
                                size="small"
                                sx={{
                                  ml: 0.5,
                                  fontSize: '0.7rem',
                                  height: 18,
                                  minWidth: 24,
                                  backgroundColor: 'var(--muted)',
                                  color: 'var(--popover-foreground)',
                                  border: '1px solid var(--border)',
                                }}
                              />
                            </Box>
                          )}
                          {getMarkerTooltipData(hoveredMarker).majorAlarmCount > 0 && (
                            <Box display="flex" alignItems="center" sx={{ minHeight: 24 }}>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  lineHeight: 0,
                                }}
                              >
                                <ErrorOutlineIcon sx={{ fontSize: 18, color: '#EAB308' }} />
                              </Box>
                              <Typography
                                variant="body2"
                                className="text-popover-foreground"
                                sx={{ lineHeight: 1.25, ml: 1, width: 64 }}
                              >
                                Major:
                              </Typography>
                              <Chip
                                label={getMarkerTooltipData(hoveredMarker).majorAlarmCount}
                                size="small"
                                sx={{
                                  ml: 0.5,
                                  fontSize: '0.7rem',
                                  height: 18,
                                  minWidth: 24,
                                  backgroundColor: 'var(--muted)',
                                  color: 'var(--popover-foreground)',
                                  border: '1px solid var(--border)',
                                }}
                              />
                            </Box>
                          )}
                          {getMarkerTooltipData(hoveredMarker).criticalAlarmCount === 0 && 
                           getMarkerTooltipData(hoveredMarker).majorAlarmCount === 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" className="text-muted-foreground">No Alarms</Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
            {/* Loading overlay */}
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
          </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Dialog showing devices without location data */}
      <Dialog
        open={showDevicesDialog}
        onClose={() => setShowDevicesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Devices Without Location Data
          <IconButton onClick={() => setShowDevicesDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List>
            {devicesWithoutLocation.map((device) => (
              <ListItem key={device.objectKey}>
                <ListItemText
                  primary={device.name}
                  secondary={`ID: ${device.objectKey}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MapDisplay;
